import { useRef, useState, type FC } from "react";
import type {
  SkynetUserEffectivePermissions,
  SkynetUserGroupMember,
  SkynetUserGroupPermissions,
} from "../../../models/RolesAndPermissionsModels";
import { Col, Row } from "react-grid-system";
import { theme } from "../../../theme";
import BodySmall from "../../atoms/typography/BodySmall";
import Body from "../../atoms/typography/Body";
import Checkbox from "../../atoms/inputs/Checkbox";
import TableRow from "../../atoms/TableRow";
import { CircleActivityIndicator } from "../../atoms/CircleActivityIndicator";
import ActionButton from "../../atoms/buttons/ActionButton";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import { HorizontalSpacer } from "../../atoms/Spacers";
import styled from "styled-components";
import ChevronButton from "../../atoms/buttons/ChevronButtons";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import { useTranslation } from "react-i18next";
import Subheading from "../../atoms/typography/Subheading";
import Divider from "../../atoms/Divider";

const SectionRow = styled(Row)<{ $isLast?: boolean }>`
  background-color: ${theme.colors.grey02};
  padding: 8px;
  border-top: 2px solid ${theme.colors.grey05};
  ${({ $isLast }) =>
    $isLast && `border-bottom: 2px solid ${theme.colors.grey05};`}
`;

type Module = keyof SkynetUserGroupPermissions;

const ALL_PERMS: Record<Module, readonly string[]> = {
  admin: ["CONFIGURATION", "LEGAL", "RULE", "USER", "USER_GROUP"],
  aml: [
    "AML_EXPORT",
    "AML_LOG",
    "AML_MONITORING",
    "AML_RISK_SCORE",
    "BLACKLIST",
    "DEBT_COLLECTION",
  ],
  client: [
    "ACCOUNT_PLAN",
    "EMAIL_ADDRESS_CONNECTION",
    "LIMITS",
    "PAYMENT",
    "SETTINGS",
    "SETTLEMENT",
    "USER",
  ],
  file: ["INVOICE_DOCUMENT_INGRESS", "SETTLEMENT"],
  invoice: [
    "FORCE_ASSESSMENT_APPROVAL",
    "INVOICE",
    "INVOICE_INTERPRETATION",
    "INVOICE_UPLOAD_UNIT",
    "EXEMPTION_VERIFIERA",
    "EXEMPTION_RECIPIENT_APPROVAL_RULES",
    "EXEMPTION_OTHER_RULES",
    "EXEMPTION_LIMITS_RULES",
    "EXEMPTION_INTERNAL_INVOICING",
    "EXEMPTION_BLACKLIST_RULES",
  ],
  onboarding: [
    "AGREEMENT",
    "BANK",
    "INTERNAL_KYC",
    "INTERNAL_SALES",
    "KYC",
    "SALES",
    "CREDIT_APPROVAL",
    "FINALIZE",
    "PROSPECT",
  ],
};

const PERM_LABELS: Record<Module, Record<string, string>> = {
  admin: {
    CONFIGURATION: "Konfigurering",
    LEGAL: "Legal",
    RULE: "Regler",
    USER: "Användare",
    USER_GROUP: "Användargrupper",
  },
  aml: {
    AML_EXPORT: "Export",
    AML_LOG: "Loggar",
    AML_MONITORING: "Monitoreringar",
    AML_RISK_SCORE: "Riskbetyg",
    BLACKLIST: "Blacklist",
    DEBT_COLLECTION: "Debt collection?",
  },
  client: {
    ACCOUNT_PLAN: "Kontoplan",
    EMAIL_ADDRESS_CONNECTION: "Epostkopplingar",
    LIMITS: "Limiter",
    PAYMENT: "Inbetalningar",
    SETTINGS: "Inställningar",
    SETTLEMENT: "Avräkningar",
    USER: "Användare",
  },
  file: {
    INVOICE_DOCUMENT_INGRESS: "Dokumenthantering",
    SETTLEMENT: "Avräkningar",
  },
  invoice: {
    FORCE_ASSESSMENT_APPROVAL: "Forcera fakturabeslut",
    INVOICE: "Fakturor",
    INVOICE_INTERPRETATION: "Fakturatolkning",
    INVOICE_UPLOAD_UNIT: "Dokumenthantering",
    EXEMPTION_VERIFIERA: "Undantag - Verifiera",
    EXEMPTION_RECIPIENT_APPROVAL_RULES: "Undantag kring mottagare",
    EXEMPTION_OTHER_RULES: "Undantag övriga",
    EXEMPTION_LIMITS_RULES: "Undantag limiter",
    EXEMPTION_INTERNAL_INVOICING: "Undantag internfakturering",
    EXEMPTION_BLACKLIST_RULES: "Undantag blacklist",
  },
  onboarding: {
    AGREEMENT: "Avtal",
    BANK: "Bank",
    INTERNAL_KYC: "Intern KYC",
    INTERNAL_SALES: "Sälj internt",
    KYC: "KYC",
    SALES: "Sälj",
    CREDIT_APPROVAL: "Kreditgodkännande",
    FINALIZE: "Slutför",
    PROSPECT: "Prospekt",
  },
};

const MODULE_LABELS: Record<Module, string> = {
  admin: "Admin",
  aml: "AML",
  client: "Klienter",
  file: "Filer",
  invoice: "Fakturor",
  onboarding: "Onboarding",
};

const MODULES: Module[] = [
  "admin",
  "aml",
  "client",
  "file",
  "invoice",
  "onboarding",
];

interface Props {
  member: SkynetUserGroupMember;
  groupPermissions: SkynetUserGroupPermissions;
  onSave: (overrides: SkynetUserEffectivePermissions) => void;
  onCancel: () => void;
  saving: boolean;
}

const MemberOverridePanel: FC<Props> = ({
  member,
  groupPermissions,
  onSave,
  onCancel,
  saving,
}) => {
  const { t } = useTranslation();

  const initOverrides = (): SkynetUserEffectivePermissions => {
    const result: SkynetUserEffectivePermissions = {};
    for (const mod of MODULES) {
      const existing =
        member.overrides?.[mod as keyof SkynetUserEffectivePermissions];
      const groupMod = groupPermissions[mod];
      if (existing) {
        result[mod as keyof SkynetUserEffectivePermissions] = {
          read: [...((existing as any).read ?? [])],
          write: [...((existing as any).write ?? [])],
        } as any;
      } else if (groupMod) {
        result[mod as keyof SkynetUserEffectivePermissions] = {
          read: [...(groupMod.defaultRead ?? [])],
          write: [...(groupMod.defaultWrite ?? [])],
        } as any;
      }
    }
    return result;
  };

  const [overrides, setOverrides] =
    useState<SkynetUserEffectivePermissions>(initOverrides);
  const initialOverridesRef = useRef(overrides);
  const isDirty =
    JSON.stringify(overrides) !== JSON.stringify(initialOverridesRef.current);
  const [openModules, setOpenModules] = useState<Module[]>([]);

  const toggleModule = (mod: Module) => {
    setOpenModules((prev) =>
      prev.includes(mod) ? prev.filter((m) => m !== mod) : [...prev, mod],
    );
  };

  const getModuleOverride = (
    mod: Module,
  ): { read: string[]; write: string[] } => {
    const modOverride = overrides[
      mod as keyof SkynetUserEffectivePermissions
    ] as any;
    return modOverride ?? { read: [], write: [] };
  };

  const togglePerm = (mod: Module, perm: string, field: "read" | "write") => {
    const current = getModuleOverride(mod);
    const arr: string[] = current[field] ?? [];
    const isRemoving = arr.includes(perm);
    const next: { read: string[]; write: string[] } = {
      ...current,
      [field]: isRemoving ? arr.filter((p) => p !== perm) : [...arr, perm],
    };
    if (field === "write" && !isRemoving) {
      const maxRead = groupPermissions[mod]?.maxRead ?? [];
      if (maxRead.includes(perm) && !next.read.includes(perm)) {
        next.read = [...next.read, perm];
      }
    }
    setOverrides({
      ...overrides,
      [mod]: next,
    });
  };

  const isOverride = (
    mod: Module,
    perm: string,
    field: "read" | "write",
  ): boolean => {
    const groupMod = groupPermissions[mod];
    const groupDefault: string[] =
      field === "read"
        ? (groupMod?.defaultRead ?? [])
        : (groupMod?.defaultWrite ?? []);
    const overrideArr: string[] = getModuleOverride(mod)[field] ?? [];
    return overrideArr.includes(perm) && !groupDefault.includes(perm);
  };

  const renderPermRow = (mod: Module, perm: string) => {
    const groupMod = groupPermissions[mod];
    const maxRead: string[] = groupMod?.maxRead ?? [];
    const maxWrite: string[] = groupMod?.maxWrite ?? [];

    if (!maxRead.includes(perm) && !maxWrite.includes(perm)) return null;

    const moduleOverride = getModuleOverride(mod);
    const readChecked = moduleOverride.read.includes(perm);
    const writeChecked = moduleOverride.write.includes(perm);
    const readIsOverride = isOverride(mod, perm, "read");
    const writeIsOverride = isOverride(mod, perm, "write");

    return (
      <TableRow key={`${mod}-${perm}`} clickable={false}>
        <Col lg={4}>
          <BodySmall>{PERM_LABELS[mod][perm]}</BodySmall>
        </Col>
        <Col lg={4} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Checkbox
            title={t("users.read")}
            checked={readChecked}
            onChange={() => togglePerm(mod, perm, "read")}
            disabled={!maxRead.includes(perm)}
          />
          {readIsOverride && (
            <CircleActivityIndicator small color={theme.colors.warning} />
          )}
        </Col>
        <Col lg={4} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Checkbox
            title={t("users.write")}
            checked={writeChecked}
            onChange={() => togglePerm(mod, perm, "write")}
            disabled={!maxWrite.includes(perm)}
          />
          {writeIsOverride && (
            <CircleActivityIndicator small color={theme.colors.warning} />
          )}
        </Col>
      </TableRow>
    );
  };

  const getAvailablePerms = (mod: Module) => {
    const groupMod = groupPermissions[mod];
    const maxRead = groupMod?.maxRead ?? [];
    const maxWrite = groupMod?.maxWrite ?? [];
    return ALL_PERMS[mod].filter(
      (p) => maxRead.includes(p) || maxWrite.includes(p),
    );
  };

  const visibleModules = MODULES.filter(
    (mod) => getAvailablePerms(mod).length > 0,
  );

  const moduleHasOverride = (mod: Module) =>
    getAvailablePerms(mod).some(
      (perm) => isOverride(mod, perm, "read") || isOverride(mod, perm, "write"),
    );

  const renderModule = (mod: Module, isLast: boolean) => {
    const availablePerms = getAvailablePerms(mod);

    return (
      <div key={mod}>
        <SectionRow
          justify="between"
          align="center"
          nogutter
          onClick={() => toggleModule(mod)}
          $isLast={isLast}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Body fontweight="500">{MODULE_LABELS[mod]}</Body>
            {moduleHasOverride(mod) && (
              <CircleActivityIndicator small color={theme.colors.warning} />
            )}
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            <ChevronButton
              type={openModules.includes(mod) ? "up" : "down"}
              onClick={() => toggleModule(mod)}
            />
          </div>
        </SectionRow>
        <SlideOutHeight isVisible={openModules.includes(mod)}>
          {availablePerms.map((perm) => renderPermRow(mod, perm))}
        </SlideOutHeight>
      </div>
    );
  };

  return (
    <div>
      <Subheading color={theme.colors.primary}>
        {t("users.handlePermissionsFor")} {member.skynetUser?.firstName}{" "}
        {member.skynetUser?.lastName}
      </Subheading>
      <HorizontalSpacer spacing={1} />
      <div
        style={{
          padding: 16,
        }}
      >
        {visibleModules.map((mod, index) =>
          renderModule(mod, index === visibleModules.length - 1),
        )}
        <HorizontalSpacer spacing={1} />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CircleActivityIndicator small color={theme.colors.warning} />
          <BodySmall color={theme.colors.textSecondary}>
            {t("users.customAccess")}
          </BodySmall>
        </div>
      </div>

      <HorizontalSpacer spacing={1} />
      <Divider />
      <HorizontalSpacer spacing={2} />
      <Row nogutter justify="end" align="center" style={{ gap: 24 }}>
        <DefaultButton title={t("common.cancel")} onClick={onCancel} />
        <ActionButton
          title={t("common.save")}
          color={theme.colors.primary}
          onClick={() => onSave(overrides)}
          loading={saving}
          disabled={!isDirty}
        />
      </Row>
    </div>
  );
};

export default MemberOverridePanel;
